/*
 * One categorical series palette, shared by every chart.
 *
 * Replaces four unrelated palettes: one colour for line charts, a nine-colour
 * bar set that appeared nowhere else, a third set in the transactions module,
 * and an eight-colour flat-UI array for avatars.
 *
 * Every step is >= 4.5:1 on white, so a series colour can carry its own label.
 * Assign IN ORDER — series 1 is always the first series. Never reorder for
 * aesthetics: the same category must keep its colour across the line, bar and
 * histogram views of the same data.
 *
 * Everything here resolves from theme.css at CALL time. getComputedStyle on
 * :root at module scope can run before the stylesheet applies, which silently
 * yields an empty palette — so these are functions, not constants.
 *
 * Spec: core-handover/04-icons-and-charts.md
 */
const cssVar = (name) => {
  if (typeof window === 'undefined' || !document?.documentElement) return '';
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
};

/*
 * Apex renders into an SVG and cannot read a CSS variable itself. It also parses
 * colours with a hex regex — hand it `rgb(...)` and it throws on a null match —
 * so channel triplets are converted to hex here.
 */
const toHex = (triplet) => {
  const parts = triplet
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 3);
  if (parts.length !== 3) return '';
  return `#${parts
    .map((n) => Number(n).toString(16).padStart(2, '0'))
    .join('')}`;
};

export const tone = (name) => {
  const v = cssVar(`--${name}`);
  if (!v) return undefined; // Apex falls back to its own default
  return v.startsWith('#') ? v : toHex(v) || undefined;
};

export const getSeries = () =>
  [1, 2, 3, 4, 5, 6, 7, 8].map((i) => cssVar(`--series-${i}`)).filter(Boolean);

const axisLabel = () => ({
  style: {
    colors: tone('neutral-500'),
    fontSize: '11px',
    fontWeight: 500,
  },
});

export const getBaseOptions = () => ({
  chart: {
    fontFamily: 'Inter, system-ui, sans-serif',
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  // Smoothed curves misrepresent monthly totals.
  stroke: { width: 2.5, curve: 'straight', lineCap: 'round' },
  grid: {
    borderColor: tone('neutral-100'),
    strokeDashArray: 0,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  dataLabels: { enabled: false },
  // Legends render in the panel header, not by Apex — Apex legends can't be
  // styled to match and they reflow the plot area.
  legend: { show: false },
  xaxis: {
    axisBorder: { color: tone('neutral-300') },
    axisTicks: { show: false },
    labels: axisLabel(),
  },
  yaxis: { labels: axisLabel() },
  tooltip: { theme: 'light', style: { fontSize: '12px' } },
});

export const getBarOptions = () => ({
  ...getBaseOptions(),
  plotOptions: { bar: { borderRadius: 2, columnWidth: '58%' } },
});
